import { useAbility } from "@casl/react";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { AbilityContext } from "@/casl.config";
import { Receive } from "@/types/general";
import { toastError } from "@helpers/utils";
import {
    useDeleteReceiptAttachmentMutation,
    useUpdateReceiptDocumentNumberMutation,
    useUploadReceiptAttachmentsMutation,
} from "@redux/features/receipts/receiptsApi";
import { useAppSelector } from "@redux/hooks";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import BaseModal from "@reusables/Modals/BaseModal";
import UIButton from "@reusables/UIButton";

import type { ReceiptDocumentItem } from "./FileRow";
import Panel from "./Panel";

export default function DetailsSection({ receipt }: { receipt: Receive.Extended }) {
    const { t } = useTranslation("", { keyPrefix: "receive" });
    const ability = useAbility(AbilityContext);
    const token = useAppSelector((state) => state.auth.tokens?.access);

    const [uploadAttachments, { isLoading: isUploading }] = useUploadReceiptAttachmentsMutation();
    const [deleteAttachment, { isLoading: isDeleting }] = useDeleteReceiptAttachmentMutation();
    const [updateDocumentNumber, { isLoading: isUpdatingDocumentNumber }] =
        useUpdateReceiptDocumentNumberMutation();

    const [attachmentForDeletion, setAttachmentForDeletion] =
        useState<Receive.Components.Attachment>();
    const [documentNumber, setDocumentNumber] = useState(receipt.document_number ?? "");

    const canUpload = ability.can("create", "receive");
    const canDelete = ability.can("delete", "receive");
    const canDownload = ability.can("view", "receive");
    const originalDocumentNumber = receipt.document_number ?? "";
    const normalizedDocumentNumber = documentNumber.trim();
    const normalizedOriginalDocumentNumber = originalDocumentNumber.trim();
    const isDocumentNumberChanged = normalizedDocumentNumber !== normalizedOriginalDocumentNumber;

    useEffect(() => {
        setDocumentNumber(receipt.document_number ?? "");
    }, [receipt.document_number]);

    const documentItems = useMemo<ReceiptDocumentItem[]>(
        () =>
            (receipt.attachments ?? []).map((attachment) => ({
                type: "remote",
                key: `${attachment.id}`,
                fileName: attachment.file_name,
                size: attachment.size,
                mimeType: attachment.mime_type,
                attachment,
            })),
        [receipt.attachments]
    );

    const handleFilesAccepted = useCallback(
        (files: File[]) => {
            if (!files.length || !canUpload) return;

            uploadAttachments({
                receiveId: receipt.id,
                attachments: files,
            })
                .unwrap()
                .then(() => {
                    toast.success(t("documents.responses.upload.success"));
                })
                .catch((e) => {
                    console.error(e);
                    toastError(e);
                });
        },
        [canUpload, receipt.id, t, uploadAttachments]
    );

    const handleDownload = useCallback(
        (item: ReceiptDocumentItem) => {
            if (item.type !== "remote" || !canDownload) return;

            downloadRemoteAttachment(item.attachment, token).catch((e) => {
                console.error(e);
                toast.error(t("documents.responses.download.error"));
            });
        },
        [canDownload, t, token]
    );

    const handleDelete = useCallback(
        (item: ReceiptDocumentItem) => {
            if (item.type !== "remote" || !canDelete) return;
            setAttachmentForDeletion(item.attachment);
        },
        [canDelete]
    );

    const handleDeletionConfirm = useCallback(() => {
        if (!attachmentForDeletion) return;

        deleteAttachment({
            receiveId: receipt.id,
            attachmentId: attachmentForDeletion.id,
        })
            .unwrap()
            .then(() => {
                toast.success(t("documents.responses.deletion.success"));
            })
            .catch((e) => {
                console.error(e);
                toastError(e);
            })
            .finally(() => {
                setAttachmentForDeletion(undefined);
            });
    }, [attachmentForDeletion, deleteAttachment, receipt.id, t]);

    const handleDocumentNumberSave = useCallback(() => {
        if (!isDocumentNumberChanged || isUpdatingDocumentNumber) return;

        updateDocumentNumber({
            receiveId: receipt.id,
            document_number: normalizedDocumentNumber || null,
        })
            .unwrap()
            .then(() => {
                toast.success(t("documents.responses.documentNumber.success"));
            })
            .catch((e) => {
                console.error(e);
                toastError(e);
            });
    }, [
        isDocumentNumberChanged,
        isUpdatingDocumentNumber,
        normalizedDocumentNumber,
        receipt.id,
        t,
        updateDocumentNumber,
    ]);

    const documentNumberAction = isDocumentNumberChanged ? (
        <BaseMaterialIconButton
            type="button"
            style="dim"
            size="small"
            icon={
                isUpdatingDocumentNumber ? (
                    <Loader2 size={16} className="animate-spin" />
                ) : (
                    <Check size={16} />
                )
            }
            tip={t("documents.actions.saveDocumentNumber")}
            tooltipPlacement="top"
            disabled={isUpdatingDocumentNumber}
            className="disabled:cursor-not-allowed disabled:opacity-60"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleDocumentNumberSave();
            }}
        />
    ) : undefined;

    return (
        <>
            <Panel
                documentNumberValue={documentNumber}
                documentNumberAction={documentNumberAction}
                files={documentItems}
                uploadDisabled={!canUpload}
                uploadDisabledReason={t("documents.permissions.upload")}
                uploadLoading={isUploading}
                deleteDisabled={!canDelete}
                deleteDisabledReason={t("documents.permissions.delete")}
                downloadDisabled={!canDownload}
                onDocumentNumberChange={setDocumentNumber}
                onFilesAccepted={handleFilesAccepted}
                onDownload={handleDownload}
                onDelete={handleDelete}
            />

            <ReceiptAttachmentDeletionModal
                attachment={attachmentForDeletion}
                isOpen={!!attachmentForDeletion}
                isLoading={isDeleting}
                onClose={() => setAttachmentForDeletion(undefined)}
                onConfirm={handleDeletionConfirm}
            />
        </>
    );
}

function ReceiptAttachmentDeletionModal({
    attachment,
    isOpen,
    isLoading,
    onClose,
    onConfirm,
}: {
    attachment?: Receive.Components.Attachment;
    isOpen: boolean;
    isLoading: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    const { t } = useTranslation("", { keyPrefix: "receive.documents.deletionModal" });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={700}
            padding="56px"
            isLoading={isLoading}
            useCloseIcon
        >
            <div className="space-y-[32px]">
                <div className="text-xl font-semibold text-center text-accent">{t("heading")}</div>

                <div className="text-center text-accent">
                    {t("subheading")}{" "}
                    <span className="bold-highlight">{attachment?.file_name}</span>?
                </div>

                <div className="grid grid-cols-2 gap-[16px]">
                    <UIButton type="button" size="lg" variant="outline" onClick={onClose}>
                        {t("buttons.cancel")}
                    </UIButton>
                    <UIButton
                        type="button"
                        size="lg"
                        variant="error"
                        className="!border-[#E25C5C] !bg-[#E25C5C] !text-white hover:!border-[#D94E4E] hover:!bg-[#D94E4E] hover:!text-white"
                        onClick={onConfirm}
                    >
                        {t("buttons.delete")}
                    </UIButton>
                </div>
            </div>
        </BaseModal>
    );
}

async function downloadRemoteAttachment(attachment: Receive.Components.Attachment, token?: string) {
    const response = await fetch(attachment.download_url, {
        method: "GET",
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error("Receipt document download failed.");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    triggerBrowserDownload(url, attachment.file_name);
    window.URL.revokeObjectURL(url);
}

function triggerBrowserDownload(url: string, fileName: string) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();
}
